import React, { useEffect, useState, useRef, lazy, memo } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, BarChart3Icon, ArrowRightIcon, ShieldIcon, FlameIcon } from 'lucide-react';
import { setBumps, setPopupOpen } from '../utils/checkoutStore';
import { trackAddToCart } from '../utils/metaPixel';
function PriceBlock({
  totalPrice,
  totalOriginalPrice
}: {
  totalPrice: number;
  totalOriginalPrice: number;
}) {
  const [spots, setSpots] = useState(15);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const savings = totalOriginalPrice - totalPrice;
  const discountPct = Math.round(savings / totalOriginalPrice * 100);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) {
        setStarted(true);
      }
    }, {
      threshold: 0.5
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);
  useEffect(() => {
    if (!started) return;
    const interval = setInterval(() => {
      setSpots(prev => prev > 3 ? prev - 1 : 3);
    }, 8000);
    return () => clearInterval(interval);
  }, [started]);
  return <div ref={ref} className="w-full grid grid-cols-2 gap-3 mb-6 rounded-2xl overflow-hidden border border-gray-200" data-id="element-402">
      {/* Left: Spots */}
      <div className="bg-white p-4 md:p-6 text-center" data-id="element-403">
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase mb-2 flex items-center justify-center gap-1.5" style={{
        color: '#e76f51'
      }} data-id="element-404">
          <FlameIcon className="w-4 h-4" data-id="element-405" /> CUPOS HOY
        </p>
        <p className="text-4xl md:text-5xl font-bold tabular-nums" style={{
        color: '#1A1614'
      }} data-id="element-406">
          {spots}
        </p>
        <p className="text-xs mt-1" style={{
        color: '#5a5248'
      }} data-id="element-407">
          Al llegar a 0 sube el precio
        </p>
      </div>
      {/* Right: Price */}
      <div className="bg-white p-4 md:p-6 text-center" data-id="element-408">
        <p className="text-xs md:text-sm font-bold tracking-wider uppercase mb-2 flex items-center justify-center gap-1.5" style={{
        color: '#07c86e'
      }} data-id="element-409">
          💰 PRECIO ACTUAL
        </p>
        <div className="flex items-center justify-center gap-2 mb-1" data-id="element-410">
          <span className="md:text-lg line-through text-[18px]" style={{
          color: '#a8a29e'
        }} data-id="element-411">
            ${totalOriginalPrice.toFixed(0)}
          </span>
          <span className="md:text-4xl font-bold text-[36px]" style={{
          color: '#e76f51'
        }} data-id="element-412">
            ${totalPrice.toFixed(2)}
          </span>
        </div>
        <div className="flex items-center justify-center gap-2" data-id="element-413">
          <span className="text-xs font-bold px-2 py-0.5 rounded-full text-white" style={{
          backgroundColor: '#e76f51'
        }} data-id="element-414">
            -{discountPct}%
          </span>
          <span className="text-xs" style={{
          color: '#5a5248'
        }} data-id="element-415">
            Ahorras ${savings.toFixed(2)} USD
          </span>
        </div>
      </div>
    </div>;
}
export function WhatsIncluded() {
  const [bump1Selected, setBump1Selected] = useState(false);
  const [bump2Selected, setBump2Selected] = useState(false);
  const basePrice = 6.39;
  const bump1Price = 7.0;
  const bump2Price = 9.0;
  const totalPrice = basePrice + (bump1Selected ? bump1Price : 0) + (bump2Selected ? bump2Price : 0);
  const hasBump = bump1Selected || bump2Selected;
  const bump1OriginalPrice = 25.0;
  const bump2OriginalPrice = 25.0;
  const totalOriginalValue = 19.0 + (bump1Selected ? bump1OriginalPrice : 0) + (bump2Selected ? bump2OriginalPrice : 0);
  const getCheckoutUrl = () => {
    if (bump1Selected && bump2Selected) {
      return 'https://homemasterglobal.com/cart/43629381582926:1';
    }
    if (bump1Selected) {
      return 'https://homemasterglobal.com/cart/43629159383118:1';
    }
    if (bump2Selected) {
      return 'https://homemasterglobal.com/cart/43629379649614:1';
    }
    return 'https://homemasterglobal.com/cart/43531718492238:1';
  };
  useEffect(() => {
    setBumps(bump1Selected, bump2Selected);
  }, [bump1Selected, bump2Selected]);
  return <>
      {/* PRUEBA DE MERCADO */}
      <section className="py-24 md:py-32 px-4 pt-[70px] pb-[70px]" style={{
      backgroundColor: '#ffffff'
    }} data-id="element-416">
        <div className="max-w-4xl mx-auto" data-id="element-417">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} data-id="element-418">
            <span className="text-sm font-bold tracking-[0.2em] uppercase mb-4 inline-block" style={{
            color: '#e76f51'
          }} data-id="element-419">
              📊 LA TENDENCIA ES REAL
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-10" style={{
            color: '#1A1614'
          }} data-id="element-420">
              El mercado ya lo confirma
            </h2>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5
          }} className="rounded-3xl overflow-hidden shadow-xl mb-12" data-id="element-421">
              <img src="https://cdn.magicpatterns.com/uploads/9aypvKAxopemJWuscKK3vw/Diseno_sin_titulo_(4).jpg" alt="Personas disfrutando cinnamon rolls XL — tendencia viral" className="w-full object-cover" style={{
              maxHeight: '420px'
            }} data-id="element-422" />
            </motion.div>

            <p className="text-xl mb-8" style={{
            color: '#5a5248'
          }} data-id="element-423">
              La repostería visual y los productos XL se han convertido en
              tendencia dominante en redes sociales.
            </p>

            <p className="text-xl font-bold italic mb-6" style={{
            color: '#e76f51',
            fontFamily: "'Playfair Display', serif"
          }} data-id="element-424">
              Las marcas que trabajan con:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10" data-id="element-425">
              {[{
              text: 'Menús rotativos.',
              img: 'https://cdn.magicpatterns.com/uploads/g5bqQeueFMQAEaxKLhQsJA/MKI_-_VAc29kF8UkcNwR3z_(1)_(1).jpg'
            }, {
              text: 'Ediciones coleccionables.',
              img: 'https://cdn.magicpatterns.com/uploads/4rDEh3KV646tp1RJYXNkmc/compri.png'
            }, {
              text: 'Formatos XL memorables.',
              img: 'https://cdn.magicpatterns.com/uploads/tgEZXD2YWvBzdsuVE27ZXW/oSDkGh-kP619f470j3k1O_(1).jpg'
            }].map((item, i) => <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100 text-center" style={{
              minHeight: '180px'
            }} data-id="element-426">
                  <img src={item.img} alt={item.text} loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" data-id="element-427" />
                  <div className="absolute inset-0" style={{
                background: 'linear-gradient(to top, rgba(26,22,20,0.75) 0%, rgba(26,22,20,0.25) 100%)'
              }} data-id="element-428" />
                  <div className="relative z-10 flex items-end justify-center h-full p-6" data-id="element-429">
                    <p className="text-lg font-bold text-white drop-shadow-lg" data-id="element-430">
                      {item.text}
                    </p>
                  </div>
                </div>)}
            </div>

            <p className="text-xl font-bold mb-4" style={{
            color: '#1A1614'
          }} data-id="element-431">
              Aumentan percepción de valor y frecuencia de compra.
            </p>
            <p className="text-lg mb-2" style={{
            color: '#5a5248'
          }} data-id="element-432">
              No es casualidad.
            </p>
            <p className="text-2xl font-bold" style={{
            color: '#e76f51'
          }} data-id="element-433">
              Es psicología aplicada al producto.
            </p>
          </motion.div>
        </div>
      </section>

      {/* EL PRODUCTO - E-COMMERCE STYLE */}
      <section id="producto" className="py-16 md:py-32 px-4" style={{
      backgroundColor: '#F5EDE4'
    }} data-id="element-434">
        <div className="max-w-6xl mx-auto" data-id="element-435">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16 items-center" data-id="element-436">
            {/* LEFT COLUMN: PRODUCT PHOTO (Hidden on mobile to control exact placement in right column) */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6
          }} className="hidden lg:block lg:w-1/2" data-id="element-437">
              <img src="https://cdn.magicpatterns.com/uploads/m4LWU9UjfJ287yR4wVx6ii/Diseno_sin_titulo_(2).png" alt="Cinnamon Rolls XL en dispositivos" loading="lazy" decoding="async" className="w-full rounded-3xl shadow-2xl object-cover max-h-[280px] lg:max-h-[600px]" data-id="element-438" />
            </motion.div>

            {/* RIGHT COLUMN: PRODUCT INFO */}
            <motion.div initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="w-full lg:w-1/2 flex flex-col items-center lg:items-start text-center lg:text-left" data-id="element-439">
              {/* 1. Subtitle */}
              <span className="md:text-sm font-bold tracking-[0.2em] uppercase mb-3 text-[14px]" style={{
              color: '#e76f51'
            }} data-id="element-440">
                DOMINA EL MÉTODO
              </span>

              {/* 2. Title */}
              <h2 className="text-3xl md:text-5xl font-bold mb-2 leading-tight" style={{
              color: '#1A1614'
            }} data-id="element-441">
                Cinnamon Rolls XL
              </h2>

              {/* 3. Subtitle Italic */}
              <p className="text-xl md:text-2xl italic mb-4" style={{
              color: '#e76f51',
              fontFamily: "'Playfair Display', serif"
            }} data-id="element-442">
                Estilo New York — Edición Coleccionable
              </p>

              {/* 4. Description */}
              <p className="text-base md:text-lg mb-6 max-w-md" style={{
              color: '#5a5248'
            }} data-id="element-443">
                Un sistema creativo diseñado para que tus cinnamon rolls tengan
                presencia de bakery profesional moderna.
              </p>

              {/* 5. Photo on Mobile (Hidden on Desktop, shown here to be between desc and includes) */}
              <div className="w-full block lg:hidden mb-6" data-id="element-444">
                <img src="https://cdn.magicpatterns.com/uploads/wXQrTWWdAM3sbbUjAamxHf/Diseno_sin_titulo_(2).jpg" alt="Cinnamon Rolls XL en dispositivos" loading="lazy" decoding="async" className="w-full rounded-2xl shadow-xl object-cover max-h-[280px]" data-id="element-445" />
              </div>

              {/* 6. Includes */}
              <div className="w-full mb-6" data-id="element-446">
                <p className="text-base md:text-lg font-bold mb-3" style={{
                color: '#1A1614'
              }} data-id="element-447">
                  Incluye:
                </p>
                <ul className="space-y-2 text-left inline-block lg:block" data-id="element-448">
                  {['50 recetas adaptadas a formato XL real', 'Método paso a paso para principiantes', 'Técnica avanzada para lograr altura estable', 'Sistema de menú rotativo y edición coleccionable', 'Arquitectura de masa y relleno profesional'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-449">
                      <CheckIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                    color: '#07c86e'
                  }} data-id="element-450" />
                      <span className="text-sm md:text-base font-medium" style={{
                    color: '#1A1614'
                  }} data-id="element-451">
                        {item}
                      </span>
                    </li>)}
                </ul>
              </div>

              {/* 7. Bonuses */}
              <div className="w-full rounded-xl p-4 mb-8 text-left inline-block lg:block" style={{
              backgroundColor: 'rgba(231,111,81,0.1)'
            }} data-id="element-452">
                <p className="text-lg md:text-xl font-bold flex gap-2 items-center justify-center" style={{
                color: '#e76f51'
              }} data-id="element-453">
                  🎁 + 3 Bonos Gratis incluidos
                </p>
              </div>

              {/* 8. Price Block — Two columns */}
              <PriceBlock totalPrice={totalPrice} totalOriginalPrice={totalOriginalValue} data-id="element-454" />

              {/* 9. CTA Button */}
              <button onClick={() => {
              if (!bump1Selected && !bump2Selected) {
                setPopupOpen(true);
              } else {
                trackAddToCart({
                  value: totalPrice,
                  currency: 'USD'
                });
                window.open(getCheckoutUrl(), '_blank', 'noopener,noreferrer');
              }
            }} className="w-full flex items-center justify-center gap-3 font-bold text-lg md:text-xl py-4 md:py-5 px-8 rounded-full transition-all duration-300 mb-4 no-underline bg-[#E76F51]" style={{
              color: '#ffffff',
              boxShadow: '0 8px 24px rgba(231,111,81,0.3)'
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = '#f08565';
              e.currentTarget.style.transform = 'scale(1.02)';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#e76f51';
              e.currentTarget.style.transform = 'scale(1)';
            }} data-id="element-455">
                Sí, quiero dominar los Cinnamon Rolls XL
                <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6" data-id="element-456" />
              </button>

              <p className="text-xs md:text-sm flex lg:justify-start gap-1.5 w-full items-center justify-center mb-6" style={{
              color: '#5a5248'
            }} data-id="element-457">
                Garantía 30 días · Descarga inmediata · Pago Seguro
              </p>

              {/* ORDER BUMPS */}
              <div className="w-full space-y-4 mb-6" data-id="element-458">
                {/* Bump 1 */}
                <div className={`relative rounded-xl border-2 transition-all duration-300 overflow-hidden cursor-pointer ${bump1Selected ? 'border-[#e76f51] shadow-md' : 'border-gray-200 shadow-sm'}`} style={{
                backgroundColor: bump1Selected ? 'rgba(231,111,81,0.06)' : '#ffffff'
              }} onClick={() => setBump1Selected(!bump1Selected)} data-id="element-459">
                  {/* Badge */}
                  <div className="text-white font-bold uppercase tracking-wider py-1.5 px-3 flex justify-center items-center gap-1 text-[12px] bg-[#0AC56E]" data-id="element-460">
                    ★ ALTAMENTE RECOMENDADO ★
                  </div>

                  <div className="p-3 md:p-4" data-id="element-461">
                    {/* Top row: checkbox + photo + title/price */}
                    <div className="flex items-center gap-2.5" data-id="element-462">
                      <div className="flex-shrink-0 relative" data-id="element-463">
                        {!bump1Selected && <span className="absolute inset-0 rounded-md animate-ping" style={{
                        backgroundColor: 'rgba(231,111,81,0.25)'
                      }} data-id="element-464" />}
                        <div className={`relative w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${bump1Selected ? 'bg-[#e76f51] border-[#e76f51]' : 'border-[#e76f51] bg-white'}`} data-id="element-465">
                          {bump1Selected && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-466" />}
                        </div>
                      </div>
                      <img src="https://cdn.magicpatterns.com/uploads/vS6XcefejdhJHHxjkBVf6h/Salsas_y_cremas_para_postres.png" alt="Salsas & Cremas Gourmet" loading="lazy" decoding="async" className="w-11 h-14 object-contain flex-shrink-0 rounded" data-id="element-467" />
                      <div className="min-w-0" data-id="element-468">
                        <h4 className="font-bold text-[#1A1614] text-[15px] leading-tight" data-id="element-469">
                          Salsas & Cremas Gourmet
                        </h4>
                        <span className="font-bold text-[14px]" style={{
                        color: '#e76f51'
                      }} data-id="element-470">
                          + $7.00
                        </span>
                      </div>
                    </div>

                    {/* Description + bullets below, aligned with photo */}
                    <div className="pl-[30px] mt-2" data-id="element-471">
                      <p className="text-[13px] text-[#5a5248] leading-snug" data-id="element-472">
                        Convierte un mismo roll en múltiples versiones
                        irresistibles.
                      </p>
                      <div className="mt-1.5 space-y-1" data-id="element-473">
                        <p className="text-xs text-[#5a5248] flex items-center gap-1.5" data-id="element-474">
                          <CheckIcon className="w-3.5 h-3.5 text-[#07c86e] flex-shrink-0" data-id="element-475" />
                          Ganaches y caramelos intensos
                        </p>
                        <p className="text-xs text-[#5a5248] flex items-center gap-1.5" data-id="element-476">
                          <CheckIcon className="w-3.5 h-3.5 text-[#07c86e] flex-shrink-0" data-id="element-477" />
                          Cremas tipo bakery
                        </p>
                        <p className="text-xs text-[#5a5248] flex items-center gap-1.5" data-id="element-478">
                          <CheckIcon className="w-3.5 h-3.5 text-[#07c86e] flex-shrink-0" data-id="element-479" />
                          Aplicación directa sobre tus rolls
                        </p>
                      </div>
                      <p className="text-xs font-bold text-[#e76f51] italic mt-1.5" data-id="element-480">
                        👉 Más sabor. Más impacto. Más deseo.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Bump 2 */}
                <div className={`relative rounded-xl border-2 transition-all duration-300 overflow-hidden cursor-pointer ${bump2Selected ? 'border-[#e76f51] shadow-md' : 'border-gray-200 shadow-sm'}`} style={{
                backgroundColor: bump2Selected ? 'rgba(231,111,81,0.06)' : '#ffffff'
              }} onClick={() => setBump2Selected(!bump2Selected)} data-id="element-481">
                  {/* Badge */}
                  <div className="text-white font-bold uppercase tracking-wider py-1.5 px-3 flex justify-center items-center gap-1 text-[12px] bg-[#0AC56E]" data-id="element-482">
                    ★ ALTAMENTE RECOMENDADO ★
                  </div>

                  <div className="p-3 md:p-4" data-id="element-483">
                    {/* Top row: checkbox + photo + title/price */}
                    <div className="flex items-center gap-2.5" data-id="element-484">
                      <div className="flex-shrink-0 relative" data-id="element-485">
                        {!bump2Selected && <span className="absolute inset-0 rounded-md animate-ping" style={{
                        backgroundColor: 'rgba(231,111,81,0.25)'
                      }} data-id="element-486" />}
                        <div className={`relative w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${bump2Selected ? 'bg-[#e76f51] border-[#e76f51]' : 'border-[#e76f51] bg-white'}`} data-id="element-487">
                          {bump2Selected && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-488" />}
                        </div>
                      </div>
                      <img src="https://cdn.magicpatterns.com/uploads/gGajHXu6zYNuJN1Lh4rrHW/Menu_de_cinnamon_rolls_XL.png" alt="Menú Rotativo XL" loading="lazy" decoding="async" className="w-11 h-14 object-contain flex-shrink-0 rounded" data-id="element-489" />
                      <div className="min-w-0" data-id="element-490">
                        <h4 className="font-bold text-[#1A1614] text-[15px] leading-tight" data-id="element-491">
                          Menú Rotativo Rolls XL
                        </h4>
                        <span className="text-sm font-bold" style={{
                        color: '#e76f51'
                      }} data-id="element-492">
                          + $9.00
                        </span>
                      </div>
                    </div>

                    {/* Description + bullets below, aligned with photo */}
                    <div className="pl-[30px] mt-2" data-id="element-493">
                      <p className="text-[13px] text-[#5a5248] leading-snug" data-id="element-494">
                        5 lineups temáticos con 15 rolls de edición limitada
                        para lanzamientos tipo bakery moderna.
                      </p>
                      <div className="mt-1.5 space-y-1" data-id="element-495">
                        <p className="text-xs text-[#5a5248] flex items-center gap-1.5" data-id="element-496">
                          <CheckIcon className="w-3.5 h-3.5 text-[#07c86e] flex-shrink-0" data-id="element-497" />
                          Sistema de "drops" que aumenta demanda
                        </p>
                        <p className="text-xs text-[#5a5248] flex items-center gap-1.5" data-id="element-498">
                          <CheckIcon className="w-3.5 h-3.5 text-[#07c86e] flex-shrink-0" data-id="element-499" />
                          Ideas listas para rotar menú
                        </p>
                        <p className="text-xs text-[#5a5248] flex items-center gap-1.5" data-id="element-500">
                          <CheckIcon className="w-3.5 h-3.5 text-[#07c86e] flex-shrink-0" data-id="element-501" />
                          Enfoque visual tipo bakery premium
                        </p>
                      </div>
                      <p className="text-xs font-bold text-[#e76f51] italic mt-1.5" data-id="element-502">
                        👉 Crea rolls que generen expectativa.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CHECKOUT SUMMARY CARD */}
              <div className="w-full rounded-2xl border border-gray-200 overflow-hidden shadow-sm bg-white" data-id="element-503">
                {/* Guarantee header */}
                <div className="flex items-center justify-center gap-2 py-3 px-4" style={{
                backgroundColor: 'rgba(7,200,110,0.1)'
              }} data-id="element-504">
                  <ShieldIcon className="w-4 h-4" style={{
                  color: '#07c86e'
                }} data-id="element-505" />
                  <span className="text-sm font-bold" style={{
                  color: '#07c86e'
                }} data-id="element-506">
                    Garantía de satisfacción de 30 días
                  </span>
                </div>

                {/* Line items */}
                <div className="px-5 pt-4 pb-2 space-y-3" data-id="element-507">
                  {/* Main product */}
                  <div className="flex items-center justify-between" data-id="element-508">
                    <span className="text-sm" style={{
                    color: '#1A1614'
                  }} data-id="element-509">
                      Cinnamon Rolls XL
                    </span>
                    <span className="text-sm font-bold" style={{
                    color: '#1A1614'
                  }} data-id="element-510">
                      ${basePrice.toFixed(2)}
                    </span>
                  </div>

                  {/* Bonuses */}
                  <div className="flex items-center justify-between" data-id="element-511">
                    <span className="text-sm" style={{
                    color: '#1A1614'
                  }} data-id="element-512">
                      3 Bonos Exclusivos
                    </span>
                    <span className="text-sm line-through" style={{
                    color: '#a8a29e'
                  }} data-id="element-513">
                      $25.00
                    </span>
                  </div>

                  {/* Bump 1 if selected */}
                  {bump1Selected && <div className="flex items-center justify-between" data-id="element-514">
                      <span className="text-sm text-[#F08565]" data-id="element-515">
                        Salsas & Cremas Gourmet
                      </span>
                      <span className="text-sm font-bold" style={{
                    color: '#1A1614'
                  }} data-id="element-516">
                        ${bump1Price.toFixed(2)}
                      </span>
                    </div>}

                  {/* Bump 2 if selected */}
                  {bump2Selected && <div className="flex items-center justify-between" data-id="element-517">
                      <span className="text-sm text-[#F08565]" data-id="element-518">
                        Menú Rotativo XL
                      </span>
                      <span className="text-sm font-bold" style={{
                    color: '#1A1614'
                  }} data-id="element-519">
                        ${bump2Price.toFixed(2)}
                      </span>
                    </div>}

                  {/* Divider */}
                  <div className="border-t border-gray-200 pt-3" data-id="element-520">
                    <div className="flex items-center justify-between" data-id="element-521">
                      <span className="text-sm font-bold" style={{
                      color: '#1A1614'
                    }} data-id="element-522">
                        Valor Total
                      </span>
                      <span className="text-sm line-through" style={{
                      color: '#a8a29e'
                    }} data-id="element-523">
                        ${totalOriginalValue.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Final price */}
                  <div className="flex items-center justify-between pb-2" data-id="element-524">
                    <span className="text-base font-bold" style={{
                    color: '#1A1614'
                  }} data-id="element-525">
                      Hoy pagas solo
                    </span>
                    <span className="text-2xl font-bold" style={{
                    color: '#e76f51'
                  }} data-id="element-526">
                      ${totalPrice.toFixed(2)}
                    </span>
                  </div>
                </div>

                {/* Bottom CTA */}
                <div className="px-5 pb-5" data-id="element-527">
                  <button onClick={() => {
                  if (!bump1Selected && !bump2Selected) {
                    setPopupOpen(true);
                  } else {
                    trackAddToCart({
                      value: totalPrice,
                      currency: 'USD'
                    });
                    window.open(getCheckoutUrl(), '_blank', 'noopener,noreferrer');
                  }
                }} className="w-full flex items-center justify-center gap-2 font-bold text-lg py-4 px-8 rounded-full transition-all duration-300 no-underline" style={{
                  backgroundColor: '#e76f51',
                  color: '#ffffff',
                  boxShadow: '0 4px 16px rgba(231,111,81,0.3)'
                }} onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#f08565';
                  e.currentTarget.style.transform = 'scale(1.02)';
                }} onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = '#e76f51';
                  e.currentTarget.style.transform = 'scale(1)';
                }} data-id="element-528">
                    Completar Compra
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BONOS GRATIS */}
      <section className="py-16 md:py-24 px-4" style={{
      backgroundColor: '#ffffff'
    }} data-id="element-529">
        <div className="max-w-4xl mx-auto" data-id="element-530">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} className="text-center mb-10" data-id="element-531">
            <span className="inline-block md:text-sm font-bold tracking-[0.2em] uppercase px-4 py-2 rounded-full mb-4 text-[14px]" style={{
            backgroundColor: 'rgba(231,111,81,0.1)',
            color: '#e76f51'
          }} data-id="element-532">
              BONOS EXCLUSIVOS
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{
            color: '#1A1614'
          }} data-id="element-533">
              Lo que recibes hoy — todo incluido
            </h2>
            <p className="text-base md:text-lg max-w-2xl mx-auto" style={{
            color: '#5a5248'
          }} data-id="element-534">
              Al adquirir tu Edición Coleccionable no solo recibes el método
              completo. También incluimos 3 bonos que multiplican tus
              resultados.
            </p>
          </motion.div>

          <div className="space-y-6" data-id="element-535">
            {[{
            num: 1,
            title: 'Master en Rellenos de Cinnamon Rolls XL',
            desc: 'Aprende a crear rellenos cremosos, abundantes y con textura perfecta que no dañen la estructura de tu roll. Desde clásicos hasta sabores en tendencia.',
            img: 'https://cdn.magicpatterns.com/uploads/rdSSuAkRiiqD1oiVumF15P/ChatGPT_Image_17_mar_2026,_05_53_55_p.m..jpg',
            price: '$9 USD'
          }, {
            num: 2,
            title: 'Master en Decoración de Cinnamon Rolls XL',
            desc: 'Técnicas de decoración profesional para que cada roll parezca salido de una vitrina de bakery moderna. Toppings, glaseados y presentación estratégica.',
            img: 'https://cdn.magicpatterns.com/uploads/5MEBS9JjGA1YmCGe7cpvFa/ChatGPT_Image_17_mar_2026,_06_04_35_p.m..jpg',
            price: '$9 USD'
          }, {
            num: 3,
            title: 'Rastreador de Tendencias de Cinnamon Rolls XL',
            desc: 'Identifica qué sabores y estilos están en tendencia real para crear ediciones limitadas que se vendan rápido y generen expectativa.',
            img: 'https://cdn.magicpatterns.com/uploads/2RkDM6n8VjpjPCPrkC7oH2/ChatGPT_Image_17_mar_2026,_06_17_01_p.m..jpg',
            price: '$7 USD'
          }].map((bonus, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="flex flex-col sm:flex-row items-center gap-5 bg-white rounded-2xl p-5 md:p-6 shadow-md border border-gray-100" data-id="element-536">
                {/* Bonus Image */}
                <img src={bonus.img} alt={bonus.title} loading="lazy" decoding="async" className="w-28 h-36 md:w-32 md:h-40 object-contain flex-shrink-0 rounded-xl" data-id="element-537" />

                {/* Bonus Info */}
                <div className="flex-1 text-center sm:text-left" data-id="element-538">
                  <p className="md:text-base font-bold tracking-wider uppercase mb-1 text-[16px]" style={{
                color: '#e76f51'
              }} data-id="element-539">
                    BONO {bonus.num}
                  </p>
                  <h3 className="text-lg md:text-xl font-bold mb-2" style={{
                color: '#1A1614'
              }} data-id="element-540">
                    {bonus.title}
                  </h3>
                  <p className="text-sm md:text-base mb-3" style={{
                color: '#5a5248'
              }} data-id="element-541">
                    {bonus.desc}
                  </p>
                </div>

                {/* Price Badge */}
                <div className="flex flex-col items-center flex-shrink-0" data-id="element-542">
                  <span className="text-sm line-through mb-1" style={{
                color: '#a8a29e'
              }} data-id="element-543">
                    {bonus.price}
                  </span>
                  <span className="text-lg font-bold px-4 py-1 rounded-full" style={{
                color: '#07c86e',
                backgroundColor: 'rgba(7,200,110,0.1)'
              }} data-id="element-544">
                    GRATIS
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
    </>;
}